<template>
  <div v-if="priorResults.length > 0 || loading" class="mt-8 pt-8 border-t border-gray-200">
    <!-- Prior Jobs Header with Toggle + list refresh -->
    <div class="flex items-center gap-2 mb-4">
      <button 
        @click="toggleExpansion" 
        class="flex-1 flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg p-4 border border-gray-200"
      >
        <div class="flex items-center">
          <h3 class="text-lg font-medium text-gray-900">Submitted Jobs</h3>
        </div>
        <svg 
          class="w-5 h-5 text-gray-500 transition-transform duration-200 ml-2" 
          :class="isExpanded ? 'rotate-180' : ''"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <button
        type="button"
        @click.stop="handleRefreshList"
        class="flex-shrink-0 p-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-600 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Check for Job Updates"
        :disabled="loading"
      >
        <svg
          v-if="!loading"
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
        </svg>
        <div v-else class="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
      </button>
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="text-center text-gray-500 py-4">
      <div class="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      <p class="mt-2 text-sm">Loading prior processed jobs...</p>
    </div>
    
    <!-- Collapsible Prior Jobs Table Content -->
    <div v-show="isExpanded && !loading">
      <div class="overflow-x-auto overflow-y-auto max-h-[360px] border border-gray-300 rounded-lg">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SubID</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dataset ID</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Processed</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Curve Count</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day Count</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Until Deletion</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="result in priorResults"
              :key="rowKey(result)"
              :class="rowClass(result)"
            >
              <td class="px-4 py-3 whitespace-nowrap text-sm font-medium" :class="primaryTextClass(result)">{{ result.subid || '—' }}</td>
              <td class="px-4 py-3 whitespace-nowrap text-sm" :class="secondaryTextClass(result)">{{ result.dataset_id || '—' }}</td>
              <td class="px-4 py-3 whitespace-nowrap text-sm" :class="secondaryTextClass(result)">{{ formatDate(result.processed_date) }}</td>
              <td class="px-4 py-3 whitespace-nowrap text-sm" :class="secondaryTextClass(result)">
                {{ isProcessing(result) ? '—' : (result.curve_count ?? 0) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm" :class="secondaryTextClass(result)">
                {{ isProcessing(result) ? '—' : (result.day_count ?? 0) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm" :class="isProcessing(result) ? 'text-gray-400' : getDeletionTimeClass(result.processed_date)">
                {{ isProcessing(result) ? 'N/A' : formatTimeUntilDeletion(result.processed_date) }}
              </td>
              <td class="px-4 py-3 whitespace-nowrap text-sm">
                <div class="flex items-center gap-3">
                  <template v-if="isProcessing(result)">
                    <span
                      class="text-amber-700 text-xs font-medium"
                      :title="result.batch_status ? `Batch status: ${result.batch_status}` : 'Job is currently processing'"
                    >
                      Processing{{ result.batch_status ? ` (${result.batch_status})` : '' }}
                    </span>
                  </template>
                  <template v-else>
                    <button
                      v-if="!result.missing"
                      @click="handleLoad(result)"
                      class="text-blue-600 hover:text-blue-800 font-medium"
                      title="Load this job's results into the dashboard"
                    >
                      Load
                    </button>
                    <span v-else class="text-red-600 text-xs font-medium" title="Results file missing in S3">
                      Missing
                    </span>
                    <button
                      @click="handleRefresh(result)"
                      class="text-green-600 hover:text-green-800 transition-colors"
                      title="Refresh lifecycle: Reset the 30-day deletion countdown for this job"
                      :disabled="refreshing === result.s3_key"
                    >
                      <svg v-if="refreshing !== result.s3_key" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                      </svg>
                      <div v-else class="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                    </button>
                    <button
                      @click="handleDelete(result)"
                      class="text-red-600 hover:text-red-800 transition-colors"
                      title="Delete this job: Permanently remove this job and its results from storage"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <BaseModal
      :isVisible="showDeleteModal"
      title="Confirm Delete"
      width="md"
      @close="closeDeleteModal"
      @confirm="confirmDelete"
    >
      <template #content>
        <p class="text-sm text-gray-700">
          Are you sure you want to delete the job for <strong>SubID {{ resultToDelete?.subid }}</strong>, <strong>Dataset {{ resultToDelete?.dataset_id }}</strong>?
        </p>
        <p class="text-sm text-gray-500 mt-2">
          This action cannot be undone.
        </p>
      </template>
      <template #footer>
        <button
          @click="closeDeleteModal"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          @click="confirmDelete"
          class="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          Delete
        </button>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import axios from 'axios'
import BaseModal from './BaseModal.vue'
import { buildApiUrl, API_ENDPOINTS } from '../config/api.js'

export default {
  name: 'PriorJobsTable',
  components: {
    BaseModal
  },
  props: {
    priorResults: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isExpanded: true,
      showDeleteModal: false,
      resultToDelete: null,
      refreshing: null // Track which result is being refreshed
    }
  },
  watch: {
    priorResults: {
      handler(newVal) {
        if (!Array.isArray(newVal) || newVal.length === 0) return
        newVal.forEach((result, index) => {
          if (result.missing === true) {
            console.warn(
              `PriorJobsTable: result ${index} (${result.subid}/${result.dataset_id}) is marked missing in S3`
            )
          }
        })
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    isProcessing(result) {
      return result?.status === 'processing'
    },
    rowKey(result) {
      if (result?.batch_job_id) return `batch-${result.batch_job_id}`
      if (result?.s3_key) return result.s3_key
      return `${result?.subid}_${result?.dataset_id}_${result?.processed_date}`
    },
    rowClass(result) {
      if (this.isProcessing(result)) return 'bg-amber-50'
      if (result.missing) return 'bg-red-50 hover:bg-red-100'
      return 'bg-blue-50 hover:bg-blue-100'
    },
    primaryTextClass(result) {
      if (this.isProcessing(result)) return 'text-amber-900'
      if (result.missing) return 'text-red-900'
      return 'text-blue-900'
    },
    secondaryTextClass(result) {
      if (this.isProcessing(result)) return 'text-amber-700'
      if (result.missing) return 'text-red-600'
      return 'text-blue-700'
    },
    toggleExpansion() {
      this.isExpanded = !this.isExpanded
    },
    handleRefreshList() {
      this.$emit('refresh-list')
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },
    formatTimeUntilDeletion(processedDate) {
      if (!processedDate) return 'N/A'
      try {
        const processed = new Date(processedDate)
        const deletionDate = new Date(processed)
        deletionDate.setDate(deletionDate.getDate() + 30) // 30 days from processed date
        
        const now = new Date()
        const diffMs = deletionDate - now
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
        
        // Note: S3 lifecycle policy deletes files after 30 days, so expired jobs shouldn't appear
        // But handle edge case gracefully if lifecycle hasn't run yet
        if (diffMs < 0) {
          return 'Expired'
        } else if (diffDays > 0) {
          return `${diffDays} day${diffDays !== 1 ? 's' : ''}`
        } else if (diffHours > 0) {
          return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`
        } else if (diffMinutes > 0) {
          return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`
        } else {
          return 'Expiring soon'
        }
      } catch (error) {
        return 'N/A'
      }
    },
    getDeletionTimeClass(processedDate) {
      if (!processedDate) return ''
      try {
        const processed = new Date(processedDate)
        const deletionDate = new Date(processed)
        deletionDate.setDate(deletionDate.getDate() + 30)
        
        const now = new Date()
        const diffMs = deletionDate - now
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
        
        // Note: S3 lifecycle policy deletes files after 30 days, so expired jobs shouldn't appear
        // But handle edge case gracefully if lifecycle hasn't run yet
        if (diffMs < 0) {
          return 'text-red-600 font-medium'
        } else if (diffDays <= 1) {
          return 'text-orange-600 font-medium'
        } else if (diffDays <= 7) {
          return 'text-yellow-600'
        } else {
          return 'text-gray-600'
        }
      } catch (error) {
        return ''
      }
    },
    handleLoad(result) {
      if (this.isProcessing(result)) return
      this.$emit('load-result', result)
    },
    async handleRefresh(result) {
      if (!result.s3_key) {
        console.error('No s3_key available for refresh')
        return
      }
      
      this.refreshing = result.s3_key
      
      try {
        // Get session_id from localStorage (same as other API calls)
        const sessionId = localStorage.getItem('session_token') || 'test-session'
        
        const response = await axios.post(
          buildApiUrl(API_ENDPOINTS.RESULTS_REFRESH),
          {
            session_id: sessionId,
            s3_key: result.s3_key
          },
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
          }
        )
        
        if (response.data.success) {
          // Emit event to refresh the list (parent component should refetch)
          this.$emit('refresh-list')
        } else {
          console.error('Failed to refresh lifecycle:', response.data.message)
          alert(`Failed to refresh lifecycle: ${response.data.message}`)
        }
      } catch (error) {
        console.error('Error refreshing lifecycle:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Failed to refresh lifecycle'
        alert(`Error: ${errorMessage}`)
      } finally {
        this.refreshing = null
      }
    },
    handleDelete(result) {
      this.resultToDelete = result
      this.showDeleteModal = true
    },
    closeDeleteModal() {
      this.showDeleteModal = false
      this.resultToDelete = null
    },
    confirmDelete() {
      if (this.resultToDelete) {
        this.$emit('delete-result', this.resultToDelete)
        this.closeDeleteModal()
      }
    }
  }
}
</script>
